import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { API } from '../App';
import { useAuthedImage, useAuthedImagePolling } from '../hooks/useAuthedImage';
import CameraCapture from '../components/CameraCapture';
import './AnalysisPage.css';
import './NewPatientPage.css';

const compressImage = (file, maxWidth = 800, quality = 0.6) =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width  = img.width  * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', quality);
    };
    img.src = URL.createObjectURL(file);
  });

const getChartItems = (t) => [
  { key: 'back_scoliose',   view: 'back', label: t('analysis.labels.back_scoliose'),   img: 'scale_scoliose.png',     rulerFraction: 0, degreeAnchors: [55, 35, 0, 35, 55] },
  { key: 'back_genou',      view: 'back', label: t('analysis.labels.back_genou'),      img: 'scale_genou_front.png',  rulerFraction: 0, degreeAnchors: [25, 15, 0, 10, 20] },
  { key: 'side_cervicales', view: 'side', label: t('analysis.labels.side_cervicales'), img: 'scale_cyphose.png',      rulerFraction: 0, degreeAnchors: [0, 47.5, 62.5, 80] },
  { key: 'side_lombaires',  view: 'side', label: t('analysis.labels.side_lombaires'),  img: 'scale_lordose.png',      rulerFraction: 0, degreeAnchors: [0, 60, 72.5, 85] },
  { key: 'side_genou',      view: 'side', label: t('analysis.labels.side_genou'),      img: 'scale_genou_sag.png',    rulerFraction: 0, degreeAnchors: [20, 10, 0, 22.5, 35] },
];

function interpolateDegrees(xNormalized, anchors) {
  const n = anchors.length;
  if (n === 1) return anchors[0];
  const centers = Array.from({ length: n }, (_, i) => (i + 0.5) / n);
  const x = Math.max(0, Math.min(1, xNormalized));
  if (x <= centers[0]) return anchors[0];
  if (x >= centers[n - 1]) return anchors[n - 1];
  for (let i = 0; i < n - 1; i++) {
    if (x >= centers[i] && x <= centers[i + 1]) {
      const frac = (x - centers[i]) / (centers[i + 1] - centers[i]);
      return anchors[i] + frac * (anchors[i + 1] - anchors[i]);
    }
  }
  return anchors[n - 1];
}

function toEstimateState(serverEstimates) {
  const out = {};
  Object.entries(serverEstimates || {}).forEach(([key, v]) => {
    out[key] = {
      xNormalized: v.x_normalized ?? 0.5,
      comment: v.comment || '',
      degrees: v.degrees,
      manuallyEdited: false,
    };
  });
  return out;
}

function toServerEstimates(estimates) {
  const out = {};
  Object.entries(estimates || {}).forEach(([key, v]) => {
    out[key] = { x_normalized: v.xNormalized, comment: v.comment };
    if (typeof v.degrees === 'number') out[key].degrees = v.degrees;
  });
  return out;
}

export default function AnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  // Présent uniquement quand on arrive depuis l'historique du patient sur
  // une consultation PASSÉE : la page passe alors en lecture seule (photos
  // et estimations de CETTE consultation précise, aucune modification
  // possible) -- cf. resolve_consultation côté backend.
  const consultationId = searchParams.get('consultation_id') || '';
  const qs = consultationId ? `?consultation_id=${consultationId}` : '';
  const qsAmp = consultationId ? `&consultation_id=${consultationId}` : '';

  const [patient,         setPatient]         = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [photos,          setPhotos]          = useState({ back: null, side: null });
  const [uploading,       setUploading]       = useState(false);
  const [uploadError,     setUploadError]     = useState('');
  const [photosReady,     setPhotosReady]     = useState(false);
  const [analyzing,       setAnalyzing]       = useState(false);
  const [estimates,       setEstimates]       = useState({});
  const [reportOpen,      setReportOpen]      = useState(false);
  const [reportText,      setReportText]      = useState('');
  const [reportLoading,   setReportLoading]   = useState(false);
  const [chatMessages,    setChatMessages]    = useState([]);
  const [chatInput,       setChatInput]       = useState('');
  const [chatSending,     setChatSending]     = useState(false);
  // Temps pris par la dernière analyse IA (postural-estimate), en ms --
  // affiché à l'écran pour avoir une échelle de référence visible et
  // repérer facilement une régression de performance.
  const [analysisDurationMs, setAnalysisDurationMs] = useState(null);
  // Idem FootAnalysisPage : casse-cache + indicateur pour le remplacement
  // d'une photo dos/profil déjà enregistrée (nouvelle consultation).
  const [photoVersion,    setPhotoVersion]    = useState(0);
  const [replacingView,   setReplacingView]   = useState(null);
  // Squelette IA (gpt-image, overlay dos+profil avec les degrés estimés
  // écrits sur l'image) : déclenché EXPLICITEMENT par le praticien via un
  // bouton dédié (cf. handleGenerateSkeleton plus bas). Avant, c'était
  // automatique et silencieux au moment de quitter la page -- impossible de
  // savoir si ça avait réellement démarré, et ça ne se déclenchait pas
  // fiablement une fois combiné au reste du flux. État (pas juste une ref)
  // car l'UI doit maintenant refléter "en cours" / "prêt" / "échec".
  const [skeletonRequested, setSkeletonRequested] = useState(false);
  // Durée (ms) de la dernière génération de squelette -- persistée côté
  // backend (postural_overlay_duration_ms), récupérée soit au chargement
  // (squelette déjà généré lors d'une session précédente), soit en
  // re-fetchant le patient une fois skeletonDone passé à true.
  const [skeletonDurationMs, setSkeletonDurationMs] = useState(null);

  const CHART_ITEMS = getChartItems(t);

  // Vues réellement enregistrées pour la consultation affichée : tout le
  // rendu (cartes photo, planches, squelette) s'y limite -- une vue absente
  // n'a ni photo à afficher, ni estimation IA, ni overlay à attendre.
  const savedViews  = (patient?.photos || []).map(p => p.view);
  const hasBackPhoto = savedViews.includes('back');
  const hasSidePhoto = savedViews.includes('side');
  const VIEW_LABELS = { back: t('analysis.view_back'), side: t('analysis.view_side') };

  const fetchPatient = () => {
    // Renvoie la promesse : handleReplacePhoto doit pouvoir attendre que
    // `patient` soit à jour (ajout d'une vue qui n'existait pas encore) avant
    // de relancer l'analyse.
    return fetch(`${API}/api/patients/${id}${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setPatient(data);
        // UNE vue suffit : dos et profil sont indépendants, le backend
        // n'évalue que les planches de la/des vue(s) fournie(s) (cf.
        // get_postural_estimate). Exiger les deux ici bloquait l'écran
        // d'upload alors que l'analyse est parfaitement possible.
        const hasBack = data.photos.some(p => p.view === 'back');
        const hasSide = data.photos.some(p => p.view === 'side');
        setPhotosReady(hasBack || hasSide);
        const savedEstimates = data.postural_estimates_validated || data.postural_estimates;
        if (savedEstimates) setEstimates(toEstimateState(savedEstimates));
        // Squelette déjà généré pour cette consultation (rechargement de
        // page après un déclenchement précédent) : pas besoin de relancer
        // gpt-image inutilement à la prochaine navigation.
        const backPhoto = data.photos.find(p => p.view === 'back');
        const sidePhoto = data.photos.find(p => p.view === 'side');
        if (backPhoto?.postural_overlay_blob || sidePhoto?.postural_overlay_blob) {
          setSkeletonRequested(true);
        }
        if (typeof data.postural_overlay_duration_ms === 'number') {
          setSkeletonDurationMs(data.postural_overlay_duration_ms);
        }
      })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPatient(); }, [id, consultationId]);

  // Lecture seule : soit demandée explicitement via ?consultation_id=...,
  // soit confirmée par le backend (is_historical) si jamais cet id ne
  // correspond plus à la consultation active entre-temps.
  const readOnly = !!consultationId;

  const analysisStarted = useRef(false);
  useEffect(() => {
    if (!readOnly && photosReady && !patient?.postural_estimates && !analysisStarted.current) {
      analysisStarted.current = true;
      handleAutoAnalyze();
    }
  }, [photosReady, patient, readOnly]);

  const handlePhoto = (view, file) => {
    if (!file) return;
    setPhotos(p => ({ ...p, [view]: { file, preview: URL.createObjectURL(file) } }));
  };

  const handleUpload = async () => {
    // Au moins une vue, pas les deux : même règle que l'analyse de la marche
    // (une vidéo suffit) et que le backend depuis que les photos sont
    // optionnelles. Seules les vues fournies sont envoyées.
    const toUpload = [['back', photos.back], ['side', photos.side]].filter(([, p]) => p);
    if (toUpload.length === 0) { setUploadError(t('analysis.photos_at_least_one')); return; }
    setUploading(true); setUploadError('');
    try {
      for (const [view, photo] of toUpload) {
        const compressed = await compressImage(photo.file);
        const pfd = new FormData();
        pfd.append('view', view);
        pfd.append('file', compressed);
        const res = await fetch(`${API}/api/patients/${id}/photo`, { method: 'POST', body: pfd });
        if (!res.ok) throw new Error(`Erreur upload ${view}`);
      }
      setLoading(true);
      fetchPatient();
    } catch (e) { setUploadError(e.message); }
    finally { setUploading(false); }
  };

  // Remplace UNE photo déjà enregistrée (dos ou profil) pour une nouvelle
  // consultation -- sans ça, l'upload initial (!photosReady) disparaît
  // définitivement dès que les 2 photos existent et rien ne permet plus de
  // les changer (cf. même correctif sur FootAnalysisPage).
  const handleReplacePhoto = async (view, file) => {
    if (!file) return;
    setReplacingView(view);
    try {
      const compressed = await compressImage(file);
      const pfd = new FormData();
      pfd.append('view', view);
      pfd.append('file', compressed);
      const res = await fetch(`${API}/api/patients/${id}/photo`, { method: 'POST', body: pfd });
      if (!res.ok) throw new Error();
      setPhotoVersion(v => v + 1);
      // Recharge le patient : sert aussi bien au remplacement qu'à l'AJOUT
      // d'une vue qui n'avait pas été photographiée (les photos étant
      // optionnelles) -- sans ça la nouvelle vue n'apparaîtrait qu'après un
      // rechargement manuel de la page.
      await fetchPatient();
      // La photo a changé : l'éventuel squelette déjà généré ne correspond
      // plus à rien, et on autorise une nouvelle génération. Après
      // fetchPatient, qui le remet à true s'il existe un ancien overlay.
      setSkeletonRequested(false);
      // L'estimation IA existante a été calculée sur l'ANCIENNE photo (ou
      // n'existait pas pour une vue qu'on vient d'ajouter).
      await handleAutoAnalyze();
    } catch (e) {
      alert(t('analysis.replace_error'));
    } finally {
      setReplacingView(null);
    }
  };

  const handleAutoAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/patients/${id}/postural-estimate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setEstimates(toEstimateState(data.estimates));
        if (typeof data.duration_ms === 'number') setAnalysisDurationMs(data.duration_ms);
      }
    } catch (e) { console.error('Auto-analyse échouée', e); }
    finally { setAnalyzing(false); }
  };

  const formatDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

  const saveTimer = useRef(null);
  const persistEstimates = (nextEstimates) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${API}/api/patients/${id}/postural-estimates/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estimates: toServerEstimates(nextEstimates) }),
      }).catch(() => {});
    }, 600);
  };

  const handleDragEstimate = (key, xNormalized) => {
    const item = CHART_ITEMS.find(i => i.key === key);
    const degrees = item?.degreeAnchors ? interpolateDegrees(xNormalized, item.degreeAnchors) : undefined;
    setEstimates(prev => {
      const next = { ...prev, [key]: { xNormalized, comment: t('analysis.manual_correction'), degrees, manuallyEdited: true } };
      persistEstimates(next);
      return next;
    });
  };

  // Construit la ligne clinique traduite pour un item donné
  const clinicalLine = (item, est) => {
    const deg = Math.round(est.degrees);
    const normal = Math.abs(deg) < 3;
    const side = est.xNormalized < 0.5 ? t('analysis.side_right') : t('analysis.side_left');
    switch (item.key) {
      case 'back_scoliose':
        return normal
          ? t('analysis.clinical.back_scoliose_normal', { deg })
          : t('analysis.clinical.back_scoliose_abnormal', { deg, side });
      case 'back_genou':
        return normal
          ? t('analysis.clinical.back_genou_normal', { deg })
          : t('analysis.clinical.back_genou_abnormal', {
              deg,
              type: est.xNormalized < 0.5 ? t('analysis.options.varum') : t('analysis.options.valgum'),
            });
      case 'side_cervicales':
        return t('analysis.clinical.side_cervicales', { deg });
      case 'side_lombaires':
        return t('analysis.clinical.side_lombaires', { deg });
      case 'side_genou':
        return normal
          ? t('analysis.clinical.side_genou_normal', { deg })
          : t('analysis.clinical.side_genou_abnormal', {
              deg,
              type: est.xNormalized < 0.5 ? t('analysis.options.recurvatum') : t('analysis.options.flexum'),
            });
      default:
        return t('analysis.clinical.default', { label: item.label, deg });
    }
  };

  // Construit le résumé clinique envoyé à l'agent IA, entièrement dans la langue de l'interface
  const buildAssessmentSummary = () => {
    return CHART_ITEMS.map(item => {
      const est = estimates[item.key];
      if (!est) return `- ${t('analysis.not_evaluated', { label: item.label })}`;
      const origin = est.manuallyEdited ? t('analysis.origin_manual') : t('analysis.origin_ai');
      const measure = (typeof est.degrees === 'number')
        ? clinicalLine(item, est)
        : t('analysis.position_scale', { label: item.label, pct: Math.round(est.xNormalized * 100) });
      const note = est.comment && !est.manuallyEdited ? ` ${t('analysis.note_label', { comment: est.comment })}` : '';
      return `- ${measure} (${origin}).${note}`;
    }).join('\n');
  };

  // Construit les labels PosturalLabels attendus par le backend
  const buildPosturalLabels = () => {
    const labels = {
      back_epaule: 'neutre', back_scoliose: 'neutre', back_bassin: 'neutre',
      back_genou: 'neutre',  back_talon: 'neutre',    side_cervicales: 'neutre',
      side_lombaires: 'neutre', side_bassin: 'neutre', side_genou: 'neutre', side_pied: 'neutre',
    };
    CHART_ITEMS.forEach(item => {
      const est = estimates[item.key];
      if (!est) return;
      const deg = typeof est.degrees === 'number' ? Math.round(est.degrees) : 0;
      const right = est.xNormalized < 0.5;
      switch (item.key) {
        case 'back_scoliose': labels.back_scoliose = deg < 3 ? 'neutre' : right ? 'droite' : 'gauche'; break;
        case 'back_genou':    labels.back_genou    = deg < 3 ? 'neutre' : right ? 'varum'  : 'valgum'; break;
        case 'side_cervicales': labels.side_cervicales = deg > 40 ? 'cyphose' : 'neutre'; break;
        case 'side_lombaires':  labels.side_lombaires  = deg > 55 ? 'lordose' : 'neutre'; break;
        case 'side_genou':    labels.side_genou    = deg < 3 ? 'neutre' : right ? 'recurvatum' : 'flexum'; break;
        default: break;
      }
    });
    return labels;
  };

  // Génération du squelette IA (dos + profil, gpt-image) : déclenchée
  // explicitement par un clic du praticien (bouton ci-dessous dans le
  // rendu), plus au moment de quitter la page. Le POST ne fait que mettre
  // la tâche en file (réponse quasi immédiate) ; la génération réelle
  // (10-40s/vue) se fait en arrière-plan côté backend et son avancement est
  // suivi ici via sondage (backOverlay / sideOverlay plus bas).
  const handleGenerateSkeleton = async () => {
    if (readOnly || skeletonRequested) return;
    setSkeletonRequested(true);
    const labels = buildPosturalLabels();
    try {
      const res = await fetch(`${API}/api/patients/${id}/postural-overlay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: id, labels }),
      });
      if (!res.ok) throw new Error();
    } catch (e) {
      // Échec du déclenchement lui-même (pas de la génération, qui a lieu
      // en arrière-plan une fois la tâche mise en file) : on redonne la
      // main pour permettre de réessayer.
      setSkeletonRequested(false);
      alert(t('analysis.skeleton_trigger_error'));
    }
  };

  const handleGoToFootAnalysis = () => {
    navigate(`/foot-analysis/${id}${qs}`);
  };

  // Suivi de la génération en cours (ou déjà terminée) : polling de
  // /postural-overlay/{view}, uniquement une fois déclenché (URL null =
  // aucune requête, cf. useAuthedImagePolling).
  // URL null pour une vue non photographiée : aucun overlay ne sera jamais
  // généré pour elle, la sonder reviendrait à attendre indéfiniment un 404
  // (et le squelette resterait éternellement "en cours").
  const backOverlay = useAuthedImagePolling(
    skeletonRequested && hasBackPhoto ? `${API}/api/patients/${id}/postural-overlay/back${qs}` : null,
    { intervalMs: 4000, maxAttempts: 90 },
  );
  const sideOverlay = useAuthedImagePolling(
    skeletonRequested && hasSidePhoto ? `${API}/api/patients/${id}/postural-overlay/side${qs}` : null,
    { intervalMs: 4000, maxAttempts: 90 },
  );
  const trackedOverlays = [
    hasBackPhoto && backOverlay,
    hasSidePhoto && sideOverlay,
  ].filter(Boolean);
  const skeletonDone   = skeletonRequested && trackedOverlays.length > 0
    && trackedOverlays.every(o => o.status === 'loaded');
  const skeletonFailed = skeletonRequested && trackedOverlays.some(o => o.status === 'failed');

  // Dès que le squelette est prêt, on récupère la durée réelle mesurée côté
  // backend (postural_overlay_duration_ms) plutôt que de chronométrer côté
  // client -- plus précis, et cohérent avec le chiffre déjà affiché sur la
  // page de bilan.
  useEffect(() => {
    if (!skeletonDone) return;
    fetch(`${API}/api/patients/${id}${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (typeof d.postural_overlay_duration_ms === 'number') setSkeletonDurationMs(d.postural_overlay_duration_ms);
      })
      .catch(() => {});
  }, [skeletonDone]);

  const handleOpenReport = async () => {
    setReportOpen(true);
    setReportLoading(true);
    try {
      const summary = buildAssessmentSummary();
      const messages = [{ role: 'user', content: `${t('analysis.report_intro')}\n${summary}` }];
      const lang = (i18n.language || 'fr').split('-')[0];
      const res = await fetch(`${API}/api/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: id, messages, lang }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setReportText(data.report);
      setChatMessages([
        { role: 'user', content: messages[0].content },
        { role: 'assistant', content: data.report },
      ]);
    } catch (e) {
      setReportText(t('analysis.report_error'));
    } finally {
      setReportLoading(false);
    }
  };

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    // Max 5 échanges par consultation (messages 0-1 = demande + rapport ;
    // les questions du praticien commencent à l'index 2). Garde-fou en plus
    // du champ désactivé côté ReportModal, et de la limite backend.
    if (chatMessages.slice(2).filter(m => m.role === 'user').length >= 5) return;
    setChatInput('');
    const nextMessages = [...chatMessages, { role: 'user', content: text }];
    setChatMessages(nextMessages);
    setChatSending(true);
    try {
      const lang = (i18n.language || 'fr').split('-')[0];
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: id, messages: nextMessages, lang }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setChatMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      setChatMessages(m => [...m, { role: 'assistant', content: t('analysis.chat_error') }]);
    } finally {
      setChatSending(false);
    }
  };

  if (loading) return <AnalysisSkeleton />;
  if (!patient) return null;

  const fullName = `${patient.first_name} ${patient.last_name.toUpperCase()}`;

  return (
    <div className="analysis-page">
      <header className="ap-header">
        <button className="back-btn" onClick={() => navigate('/')}>{t('common.back')}</button>
        <div className="ap-title-row">
          <div>
            <p className="ap-eyebrow">{t('analysis.eyebrow')}</p>
            <h1 className="ap-title">{fullName}</h1>
          </div>
          <div className="ap-date">
            {patient.created_at && new Date(patient.created_at)
              .toLocaleDateString(i18n.language, { day: '2-digit', month: 'long', year: 'numeric' })}
          </div>
        </div>
      </header>

      {readOnly && (
        <div className="historical-banner">{t('analysis.readonly_banner')}</div>
      )}

      {!photosReady && !readOnly && (
        <section className="upload-section">
          <p className="upload-title">{t('analysis.photos_required_title')}</p>
          <p className="upload-hint">{t('analysis.photos_optional_hint')}</p>
          <div className="postural-upload-grid">
            {['back', 'side'].map(view => (
              <PosturalUploadSlot key={view} view={view} label={VIEW_LABELS[view]} t={t}
                value={photos[view]} onChange={file => handlePhoto(view, file)} />
            ))}
          </div>
          {uploadError && <div className="np-error">{uploadError}</div>}
          <div className="upload-actions">
            <button className="btn-primary" onClick={handleUpload}
              disabled={uploading || (!photos.back && !photos.side)}>
              {uploading ? t('analysis.uploading') : t('analysis.analyze_btn')}
            </button>
          </div>
        </section>
      )}

      {!photosReady && readOnly && (
        <section className="upload-section">
          <p className="upload-title">{t('analysis.no_data_historical')}</p>
        </section>
      )}

      {photosReady && (
        <section className="postural-section">
          {analyzing && (
            <div className="postural-analyzing-banner">
              <div className="scan-bar" />
              <span className="dot-pulse" />
              <span>{t('analysis.ai_analyzing')}</span>
            </div>
          )}
          {!analyzing && analysisDurationMs !== null && (
            <div className="ai-timing-note">
              {t('analysis.ai_duration', { time: formatDuration(analysisDurationMs) })}
            </div>
          )}

          {/* Une ligne par vue PHOTOGRAPHIÉE. Une vue absente n'affiche ni
              carte photo (l'URL /annotated/{view} renverrait 404) ni planches
              (aucune estimation IA n'existe pour elle) : à la place, une carte
              qui le dit et permet d'ajouter la vue à la volée. */}
          {[['back', t('analysis.view_back_label'), hasBackPhoto],
            ['side', t('analysis.view_side_label'), hasSidePhoto]].map(([view, label, hasPhoto]) => (
            hasPhoto ? (
              <div className="postural-row" key={view}>
                <PosturalPhotoCard
                  patientId={id} view={view} label={label} t={t}
                  qs={qs}
                  photoVersion={photoVersion} replacing={replacingView === view}
                  onReplace={readOnly ? null : file => handleReplacePhoto(view, file)}
                />
                <div className="skeleton-stack">
                  {CHART_ITEMS.filter(item => item.view === view).map(item => (
                    <ChartCard key={item.key} item={item} value={estimates[item.key]} readOnly={readOnly}
                      onDrag={xNorm => handleDragEstimate(item.key, xNorm)} t={t} />
                  ))}
                </div>
              </div>
            ) : (
              <MissingViewRow key={view} label={label} t={t}
                adding={replacingView === view}
                onAdd={readOnly ? null : file => handleReplacePhoto(view, file)} />
            )
          ))}

          {!readOnly && (
            <div className="skeleton-gen-panel">
              <div className="skeleton-gen-header">
                <span className="skeleton-gen-title">{t('analysis.skeleton_title')}</span>
                {!skeletonRequested && (
                  <button className="btn-secondary" onClick={handleGenerateSkeleton}>
                    {t('analysis.skeleton_generate_btn')}
                  </button>
                )}
                {skeletonRequested && !skeletonDone && !skeletonFailed && (
                  <span className="skeleton-gen-status generating">
                    <span className="dot-pulse" /> {t('analysis.skeleton_generating')}
                  </span>
                )}
                {skeletonDone && (
                  <span className="skeleton-gen-status done">
                    ✓ {skeletonDurationMs != null
                      ? t('analysis.skeleton_done_duration', { time: formatDuration(skeletonDurationMs) })
                      : t('analysis.skeleton_done')}
                  </span>
                )}
                {skeletonFailed && (
                  <span className="skeleton-gen-status failed">{t('analysis.skeleton_failed')}</span>
                )}
              </div>
              {skeletonRequested && (
                <div className="skeleton-gen-thumbs">
                  {hasBackPhoto && (
                    <SkeletonThumb label={t('analysis.view_back_label')} status={backOverlay.status} blobUrl={backOverlay.blobUrl} />
                  )}
                  {hasSidePhoto && (
                    <SkeletonThumb label={t('analysis.view_side_label')} status={sideOverlay.status} blobUrl={sideOverlay.blobUrl} />
                  )}
                </div>
              )}
            </div>
          )}

          {!readOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
              <button className="btn-primary generate-btn" onClick={handleOpenReport} disabled={analyzing}>
                {t('analysis.report_btn')}
              </button>
            </div>
          )}
        </section>
      )}

      {reportOpen && (
        <ReportModal
          t={t}
          loading={reportLoading}
          reportText={reportText}
          messages={chatMessages}
          chatInput={chatInput}
          onChatInputChange={setChatInput}
          onSend={handleSendChat}
          sending={chatSending}
          onClose={() => setReportOpen(false)}
        />
      )}

      {photosReady && (
        <div className="foot-analysis-btn-wrap">
          <button className="btn-primary" onClick={handleGoToFootAnalysis}>
            {t('analysis.foot_analysis_btn')}
          </button>
        </div>
      )}
    </div>
  );
}

function PosturalUploadSlot({ view, label, value, onChange, t }) {
  const inputRef = useRef();
  // Deux entrées pour une même vue : fichier (photo prise au téléphone puis
  // transférée) ou caméra en direct (plus rapide en consultation).
  const [camOpen, setCamOpen] = useState(false);
  return (
    <div className={`upload-slot ${value ? 'has-photo' : ''}`}>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => onChange(e.target.files[0])} />
      {value ? (
        // Tout l'aperçu reste cliquable pour rouvrir le sélecteur de fichier
        // (comportement d'origine) ; seul le bouton caméra arrête la
        // propagation, sinon il déclencherait aussi le sélecteur.
        <div className="slot-preview" onClick={() => inputRef.current.click()}>
          <img src={value.preview} alt={label} />
          <div className="slot-overlay"><span>{t('analysis.upload_change')}</span></div>
          <div className="slot-check">✓</div>
          <button type="button" className="live-btn slot-live-btn"
            onClick={e => { e.stopPropagation(); setCamOpen(true); }}>
            {t('camera.live_btn')}
          </button>
        </div>
      ) : (
        <div className="slot-empty">
          <span className="slot-label">{label}</span>
          <div className="slot-inputs">
            <button type="button" className="slot-cta-btn" onClick={() => inputRef.current.click()}>
              {t('analysis.upload_choose')}
            </button>
            <button type="button" className="live-btn" onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </div>
        </div>
      )}
      <div className="slot-footer">
        <span className={`slot-status ${value ? 'ok' : 'pending'}`}>
          {value ? t('analysis.slot_ready') : t('analysis.slot_pending')}
        </span>
        <span>{label.toUpperCase()}</span>
      </div>
      {camOpen && (
        <CameraCapture label={label} onClose={() => setCamOpen(false)}
          onCapture={file => onChange(file)} />
      )}
    </div>
  );
}

// Vue non photographiée. Les photos étant optionnelles, l'écran d'analyse
// doit le dire explicitement (sinon on ne distingue pas "pas photographié" de
// "erreur de chargement") et permettre d'ajouter la vue sans avoir à ouvrir
// une nouvelle consultation.
function MissingViewRow({ label, t, adding, onAdd }) {
  const inputRef = useRef();
  const [camOpen, setCamOpen] = useState(false);
  return (
    <div className="postural-row postural-row--missing">
      <div className="missing-view-card">
        <span className="missing-view-title">{t('analysis.view_not_taken')} — {label}</span>
        <span className="missing-view-hint">{t('analysis.view_not_taken_hint')}</span>
        {onAdd && (adding ? (
          <span className="missing-view-adding"><span className="dot-pulse" /> {t('analysis.uploading')}</span>
        ) : (
          <div className="slot-inputs">
            <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { onAdd(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="slot-cta-btn" onClick={() => inputRef.current.click()}>
              {t('analysis.add_photo')}
            </button>
            <button type="button" className="live-btn" onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </div>
        ))}
      </div>
      {camOpen && (
        <CameraCapture label={label} onClose={() => setCamOpen(false)}
          onCapture={file => onAdd(file)} />
      )}
    </div>
  );
}

function PosturalPhotoCard({ patientId, view, label, t, overlayUrl, qs, photoVersion, replacing, onReplace }) {
  const inputRef = useRef();
  const [camOpen, setCamOpen] = useState(false);
  const photoUrl = `${API}/api/patients/${patientId}/annotated/${view}?v=${photoVersion ?? 0}${qs ? '&' + qs.slice(1) : ''}`;
  // <img src=...> ne peut pas envoyer le jeton d'authentification -- on
  // passe par fetch() (intercepté globalement pour y ajouter le jeton) puis
  // par une URL objet locale. Le hook réagit déjà à l'URL (photoVersion en
  // fait partie), donc plus besoin d'un useEffect séparé pour réinitialiser
  // le statut.
  const { status: photoStatus, blobUrl } = useAuthedImage(overlayUrl || photoUrl);

return (
    <div className="postural-photo-card">
      <div className="postural-photo-wrap" style={{ position: 'relative' }}>
        {(photoStatus === 'loading' || replacing) && (
          <div className="photo-loading">
            <div className="scan-bar" />
            <div className="loading-pill"><span className="dot-pulse" /> {replacing ? t('analysis.replacing_photo') : t('analysis.photo_analyzing_yolo')}</div>
          </div>
        )}
        {blobUrl && (
          <img
            src={blobUrl}
            alt={view}
            className="photo-img-base"
            style={{ opacity: photoStatus === 'loaded' && !replacing ? 1 : 0 }}
          />
        )}
        {/* Remplacement de photo (nouvelle consultation) : sans ce bouton,
            aucun moyen dans l'UI de changer une photo déjà enregistrée. */}
        {onReplace && !replacing && (
          <>
            <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { onReplace(e.target.files[0]); e.target.value = ''; }} />
            <button type="button" className="foot-photo-change-btn"
              onClick={() => inputRef.current.click()}>
              {t('analysis.change_photo')}
            </button>
            {/* Reprise directe à la caméra, sans passer par un fichier. */}
            <button type="button" className="live-btn photo-live-btn"
              onClick={() => setCamOpen(true)}>
              {t('camera.live_btn')}
            </button>
          </>
        )}
      </div>
      {camOpen && (
        <CameraCapture label={label} onClose={() => setCamOpen(false)}
          onCapture={file => onReplace(file)} />
      )}
      <div className="photo-foot">
        <span className="view-lbl">{label}</span>
        <span className={`status-pill ${photoStatus}`}>
          {photoStatus === 'loading' && t('analysis.status_analyzing')}
          {photoStatus === 'loaded'  && t('analysis.status_processed')}
          {photoStatus === 'error'   && t('analysis.status_error')}
        </span>
      </div>
    </div>
  );
}

// Miniature affichée dans le panneau de génération du squelette : barre de
// progression animée tant que l'image n'est pas prête, remplacée par
// l'image dès qu'elle l'est.
function SkeletonThumb({ label, status, blobUrl }) {
  return (
    <div className={`skeleton-thumb skeleton-thumb--${status}`}>
      <div className="skeleton-thumb-body">
        {status === 'loaded' && blobUrl ? (
          <img src={blobUrl} alt={label} />
        ) : (
          <div className="skeleton-thumb-progress">
            <div className="skeleton-thumb-bar" />
          </div>
        )}
      </div>
      <span className="skeleton-thumb-label">{label}</span>
    </div>
  );
}

function ChartCard({ item, value, onDrag, t, readOnly }) {
  const wrapRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const rulerFraction = item.rulerFraction ?? 0.07;

  const clamped = value ? Math.max(0, Math.min(1, value.xNormalized)) : null;
  const displayPct = clamped !== null ? (rulerFraction + clamped * (1 - rulerFraction)) * 100 : null;

  const moveTo = (clientX) => {
    if (readOnly) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xNorm = Math.max(0, Math.min(1, (frac - rulerFraction) / (1 - rulerFraction)));
    onDrag(xNorm);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => moveTo(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging]);

  const startDrag = (e) => {
    if (readOnly) return;
    e.preventDefault();
    moveTo(e.touches ? e.touches[0].clientX : e.clientX);
    setDragging(true);
  };

  return (
    <div className="chart-card">
      <div
        className="skeleton-img-wrap"
        ref={wrapRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{ cursor: readOnly ? 'default' : undefined }}
      >
        <img src={`${API}/api/skeleton-ref/${item.img}`} alt={item.label} draggable={false} />
        {displayPct !== null && (
          <div
            className={`estimate-line ${dragging ? 'dragging' : ''}`}
            style={{ left: `${displayPct}%`, display: 'block' }}
          />
        )}
      </div>
      <div className="skeleton-label">{item.label}</div>
      <div className="estimate-hint">{t('analysis.drag_hint')}</div>
      <div className="estimate-comment">
        {value ? (
          typeof value.degrees === 'number'
            ? `${value.manuallyEdited ? '✋ ' : ''}${Math.round(value.degrees)}°`
            : (value.manuallyEdited ? `✋ ${value.comment}` : value.comment)
        ) : ''}
      </div>
    </div>
  );
}

function ReportModal({ t, loading, reportText, messages, chatInput, onChatInputChange, onSend, sending, onClose }) {
  const questionsUsed = messages.slice(2).filter(m => m.role === 'user').length;
  const limitReached = questionsUsed >= 5;
  return (
    <div className="report-overlay" onClick={onClose}>
      <div className="report-modal" onClick={e => e.stopPropagation()}>
        <div className="report-modal-header">
          <div>
            <div className="report-modal-title">{t('analysis.report_title')}</div>
            <div className="report-modal-sub">{t('analysis.report_subtitle')}</div>
          </div>
          <button className="btn-ghost small" onClick={onClose}>✕</button>
        </div>
        <div className="report-content">
          {loading ? (
            <div className="loading-pill"><span className="dot-pulse" /> {t('analysis.report_generating')}</div>
          ) : (
            <ReactMarkdown>{reportText}</ReactMarkdown>
          )}
          {!loading && messages.length > 0 && (
            <div className="chat-messages" style={{ marginTop: 20 }}>
              {messages.slice(2).map((m, i) => (
                <div key={i} className={`chat-msg chat-${m.role}`}>{m.content}</div>
              ))}
            </div>
          )}
        </div>
        {!loading && !limitReached && (
          <div className="chat-input-row" style={{ padding: '16px 24px' }}>
            <input
              type="text"
              value={chatInput}
              onChange={e => onChatInputChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') onSend(); }}
              placeholder={t('analysis.chat_placeholder')}
            />
            <button className="btn-primary small" onClick={onSend} disabled={sending}>
              {sending ? '…' : t('analysis.chat_send')}
            </button>
          </div>
        )}
        {!loading && limitReached && (
          <div className="chat-limit-note" style={{ padding: '16px 24px', color: 'var(--text-dim)', fontSize: 12, textAlign: 'center' }}>
            {t('analysis.chat_limit', 'Limite de 5 échanges atteinte pour cette consultation.')}
          </div>
        )}
      </div>
    </div>
  );
}

function AnalysisSkeleton() {
  return (
    <div className="analysis-page">
      <div className="ap-header" style={{ marginBottom: 32 }}>
        <div className="sk sk-line" style={{ width: 80, height: 12, marginBottom: 16 }} />
        <div className="sk sk-line" style={{ width: 240, height: 32 }} />
      </div>
      <div className="postural-row">
        <div className="sk" style={{ height: 400, flex: 1, borderRadius: 12 }} />
        <div className="sk" style={{ height: 400, width: 320, borderRadius: 12 }} />
      </div>
    </div>
  );
}